import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, MapPin, X, ChevronLeft, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [relatedProjects, setRelatedProjects] = useState([]);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await axios.get(`${API}/projects/${projectId}`);
        setProject(response.data);
        
        // Fetch related projects in same category
        const allProjects = await axios.get(`${API}/projects?category=${response.data.category}`);
        setRelatedProjects(allProjects.data.filter(p => p.id !== projectId).slice(0, 3));
      } catch (err) {
        setError("Project not found");
        console.error("Error fetching project:", err);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (projectId) {
      fetchProject();
    }
  }, [projectId]);

  // Get all images for the gallery
  const getAllImages = () => {
    if (!project) return [];
    const images = [project.image_url];
    if (project.gallery_images && project.gallery_images.length > 0) {
      images.push(...project.gallery_images);
    }
    if (project.before_image_url) {
      images.push(project.before_image_url);
    }
    if (project.after_image_url) {
      images.push(project.after_image_url);
    }
    return images.filter(Boolean);
  };

  const allImages = getAllImages();

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  };

  const nextImage = () => {
    setLightboxIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = () => {
    setLightboxIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center">
        <h1 className="text-white text-2xl mb-4">Project Not Found</h1>
        <Link to="/projects" className="text-[#7ED4C6] hover:underline flex items-center gap-2">
          <ArrowLeft size={18} />
          Back to Projects
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="project-detail-page">
      {/* Hero Section with Main Image */}
      <section className="relative h-[60vh] overflow-hidden">
        <img
          src={project.image_url}
          alt={project.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/50 to-transparent" />
        
        {/* Back Button */}
        <div className="absolute top-8 left-8 z-10">
          <button
            onClick={() => navigate('/projects')}
            className="flex items-center gap-2 text-white bg-[#0A0A0A]/80 px-4 py-2 hover:bg-[#7ED4C6] hover:text-[#0A0A0A] transition-colors"
            data-testid="back-to-projects"
          >
            <ArrowLeft size={18} />
            Back to Projects
          </button>
        </div>

        {/* Project Title Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
          <div className="max-w-7xl mx-auto">
            <span className="text-[#7ED4C6] text-sm uppercase tracking-[0.3em] mb-2 block">
              {project.category}
            </span>
            <h1 className="font-['Oswald'] text-4xl md:text-6xl font-bold text-white mb-4">
              {project.title}
            </h1>
            {project.location && (
              <p className="flex items-center gap-2 text-[#A1A1AA] text-lg">
                <MapPin size={18} className="text-[#7ED4C6]" />
                {project.location}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Project Details */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Description */}
            <div className="lg:col-span-2">
              <h2 className="font-['Oswald'] text-2xl text-white mb-6">Project Overview</h2>
              <div className="section-divider mb-6" />
              <p className="text-[#A1A1AA] text-lg leading-relaxed">
                {project.description}
              </p>
            </div>

            {/* Project Info Sidebar */}
            <div className="bg-[#121212] border border-[#2A2A2A] p-6 h-fit">
              <h3 className="font-['Oswald'] text-xl text-white mb-4">Project Details</h3>
              <div className="space-y-4">
                <div>
                  <span className="text-[#7ED4C6] text-xs uppercase tracking-wider">Category</span>
                  <p className="text-white capitalize">{project.category}</p>
                </div>
                {project.location && (
                  <div>
                    <span className="text-[#7ED4C6] text-xs uppercase tracking-wider">Location</span>
                    <p className="text-white">{project.location}</p>
                  </div>
                )}
                <div>
                  <span className="text-[#7ED4C6] text-xs uppercase tracking-wider">Photos</span>
                  <p className="text-white">{allImages.length} images</p>
                </div>
              </div>
              
              <Link
                to="/contact"
                className="btn-primary w-full mt-6 text-center block"
                data-testid="project-contact-btn"
              >
                Start Similar Project
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      {allImages.length > 1 && (
        <section className="py-16 bg-[#121212]" data-testid="project-gallery">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="font-['Oswald'] text-2xl text-white mb-8">Project Gallery</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {allImages.map((img, index) => (
                <button
                  key={index}
                  onClick={() => openLightbox(index)}
                  className="aspect-square overflow-hidden group relative"
                  data-testid={`gallery-image-${index}`}
                >
                  <img
                    src={img}
                    alt={`${project.title} - Image ${index + 1}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-[#0A0A0A]/0 group-hover:bg-[#0A0A0A]/40 transition-colors flex items-center justify-center">
                    <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity text-sm uppercase tracking-wider">
                      View
                    </span>
                  </div>
                  {index === 0 && (
                    <span className="absolute top-2 left-2 bg-[#7ED4C6] text-[#0A0A0A] text-xs px-2 py-1 uppercase">
                      Main
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Before/After Comparison Slider */}
      {project.before_image_url && project.after_image_url && (
        <BeforeAfterSlider 
          beforeImage={project.before_image_url}
          afterImage={project.after_image_url}
          onImageClick={openLightbox}
          allImages={allImages}
        />
      )}

      {/* Related Projects */}
      {relatedProjects.length > 0 && (
        <section className="py-16 bg-[#121212] border-t border-[#2A2A2A]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex justify-between items-center mb-8">
              <h2 className="font-['Oswald'] text-2xl text-white">Related Projects</h2>
              <Link to="/projects" className="text-[#7ED4C6] text-sm hover:underline flex items-center gap-2">
                View All Projects <ArrowRight size={16} />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedProjects.map((relProject) => (
                <Link
                  key={relProject.id}
                  to={`/projects/${relProject.id}`}
                  className="group relative aspect-[4/3] overflow-hidden"
                >
                  <img
                    src={relProject.image_url}
                    alt={relProject.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent flex flex-col justify-end p-6">
                    <span className="text-[#7ED4C6] text-xs uppercase tracking-wider">{relProject.category}</span>
                    <h3 className="font-['Oswald'] text-xl text-white">{relProject.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-[#7ED4C6]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-['Oswald'] text-3xl md:text-4xl font-bold text-[#0A0A0A] mb-4">
            Ready to Start Your Project?
          </h2>
          <p className="text-[#0A0A0A]/80 text-lg mb-8">
            Let's discuss how we can bring your vision to life with the same quality and craftsmanship.
          </p>
          <Link
            to="/consultation"
            className="bg-[#0A0A0A] text-white font-['Oswald'] font-semibold uppercase tracking-wider px-8 py-4 hover:bg-[#121212] transition-colors inline-flex items-center gap-2"
          >
            Schedule a Consultation
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 bg-[#0A0A0A]/95 z-50 flex items-center justify-center"
          onClick={closeLightbox}
          data-testid="lightbox"
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 text-white hover:text-[#7ED4C6] z-10"
            data-testid="lightbox-close"
          >
            <X size={32} />
          </button>

          {/* Image Counter */}
          <div className="absolute top-6 left-6 text-white text-sm">
            {lightboxIndex + 1} / {allImages.length}
          </div>

          {/* Previous Button */}
          {allImages.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className="absolute left-4 md:left-8 text-white hover:text-[#7ED4C6] p-2 bg-[#0A0A0A]/50 hover:bg-[#0A0A0A]/80"
              data-testid="lightbox-prev"
            >
              <ChevronLeft size={40} />
            </button>
          )}

          {/* Image */}
          <img
            src={allImages[lightboxIndex]}
            alt={`${project.title} - Image ${lightboxIndex + 1}`}
            className="max-h-[85vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Next Button */}
          {allImages.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className="absolute right-4 md:right-8 text-white hover:text-[#7ED4C6] p-2 bg-[#0A0A0A]/50 hover:bg-[#0A0A0A]/80"
              data-testid="lightbox-next"
            >
              <ChevronRight size={40} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Before/After Comparison Slider Component
function BeforeAfterSlider({ beforeImage, afterImage, onImageClick, allImages }) {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = React.useRef(null);

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.min(Math.max((x / rect.width) * 100, 0), 100);
    setSliderPosition(percentage);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    handleMove(e.clientX);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    handleMove(e.touches[0].clientX);
  };

  React.useEffect(() => {
    const handleMouseUp = () => setIsDragging(false);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchend', handleMouseUp);
    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, []);

  return (
    <section className="py-16 bg-[#0A0A0A]" data-testid="before-after-slider">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-8">
          <h2 className="font-['Oswald'] text-2xl text-white mb-2">Before & After</h2>
          <p className="text-[#A1A1AA] text-sm">Drag the slider to compare the transformation</p>
        </div>
        
        <div
          ref={containerRef}
          className="relative aspect-video overflow-hidden cursor-ew-resize select-none"
          onMouseMove={handleMouseMove}
          onTouchMove={handleTouchMove}
          onMouseDown={() => setIsDragging(true)}
          onTouchStart={() => setIsDragging(true)}
        >
          {/* After Image (Background) */}
          <img
            src={afterImage}
            alt="After"
            className="absolute inset-0 w-full h-full object-cover"
          />
          
          {/* Before Image (Clipped) */}
          <div
            className="absolute inset-0 overflow-hidden"
            style={{ width: `${sliderPosition}%` }}
          >
            <img
              src={beforeImage}
              alt="Before"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ width: `${100 / (sliderPosition / 100)}%`, maxWidth: 'none' }}
            />
          </div>
          
          {/* Slider Handle */}
          <div
            className="absolute top-0 bottom-0 w-1 bg-white cursor-ew-resize z-10"
            style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
          >
            {/* Handle Circle */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg">
              <div className="flex items-center gap-1">
                <ChevronLeft size={16} className="text-[#0A0A0A]" />
                <ChevronRight size={16} className="text-[#0A0A0A]" />
              </div>
            </div>
          </div>
          
          {/* Labels */}
          <div className="absolute top-4 left-4 bg-[#0A0A0A]/80 text-white text-xs px-3 py-1 uppercase tracking-wider pointer-events-none">
            Before
          </div>
          <div className="absolute top-4 right-4 bg-[#7ED4C6] text-[#0A0A0A] text-xs px-3 py-1 uppercase tracking-wider pointer-events-none">
            After
          </div>
        </div>
        
        {/* Click to view full size */}
        <div className="flex justify-center gap-4 mt-4">
          <button
            onClick={() => onImageClick(allImages.indexOf(beforeImage))}
            className="text-[#A1A1AA] text-sm hover:text-white flex items-center gap-2"
          >
            View Before Full Size
          </button>
          <span className="text-[#2A2A2A]">|</span>
          <button
            onClick={() => onImageClick(allImages.indexOf(afterImage))}
            className="text-[#A1A1AA] text-sm hover:text-white flex items-center gap-2"
          >
            View After Full Size
          </button>
        </div>
      </div>
    </section>
  );
}
